import { TraceLevel } from "@ms/uno-telemetry/lib/local/events/Trace.event";
import { Temporal } from "temporal-polyfill-lite";
import { getCurrentLocale } from "./DateUtilities-temporal";
import { resolveTimeZoneCity } from "./TimeZoneCityLocalizationUtilities";
export { setTimeZoneCityResolver } from "./TimeZoneCityLocalizationUtilities";
/** Derive a human-friendly city name from an IANA id (last segment, underscores → spaces). */ function cityFromIanaId(id) {
    const segments = id.split("/");
    return (segments[segments.length - 1] ?? id).replace(/_/g, " ");
}
function getTimeZoneCity(id) {
    return resolveTimeZoneCity(id) ?? cityFromIanaId(id);
}
/**
 * Resolve a generic long display name for a time zone (e.g. "Pacific Time",
 * or "hora del Pacífico" in Spanish). Returns `null` when the platform falls
 * back to an offset string like "GMT-8" – in that case the caller should
 * omit the region prefix.
 */ export function genericTimeZoneName(timeZone, atDate, _locale) {
    const formatter = new Intl.DateTimeFormat(getCurrentLocale(), {
        timeZone,
        // `longGeneric` collapses standard/daylight variants into a single
        // name (e.g. "Pacific Time" instead of "Pacific Standard Time").
        timeZoneName: "longGeneric"
    });
    const value = formatter.formatToParts(atDate).find((p)=>p.type === "timeZoneName")?.value;
    if (!value) {
        return null;
    }
    // When no generic name exists, browsers return an offset like "GMT-8".
    if (value.startsWith("GMT")) {
        return null;
    }
    return value;
}
/**
 * Read the raw "GMT", "GMT-8", or "GMT-08:00" string the platform returns for
 * a time zone's current UTC offset. Locale-independent (`longOffset` always
 * returns the GMT form in any locale, so we pin `en-US`). Returns `null` if
 * the platform cannot produce an offset for the given zone.
 */ function readGmtOffsetString(timeZone, atDate) {
    const formatter = new Intl.DateTimeFormat("en-US", {
        timeZone,
        timeZoneName: "longOffset"
    });
    const raw = formatter.formatToParts(atDate).find((p)=>p.type === "timeZoneName")?.value ?? "";
    return raw.startsWith("GMT") ? raw : null;
}
/** UTC offset (in minutes) for a time zone at a given moment. Used for sorting. */ function getUtcOffsetMinutes(timeZone, atDate) {
    const raw = readGmtOffsetString(timeZone, atDate);
    // Raw is null, "GMT", "GMT-8", or "GMT-08:00". Parse without regex to avoid
    // backtracking-unsafe patterns flagged by lint.
    if (!raw || raw.length === 3) {
        return 0;
    }
    const sign = raw[3] === "-" ? -1 : 1;
    const body = raw.slice(4);
    const [h, m] = body.split(":");
    const hours = Number(h);
    const minutes = m ? Number(m) : 0;
    if (!Number.isFinite(hours) || !Number.isFinite(minutes)) {
        return 0;
    }
    return sign * (hours * 60 + minutes);
}
/**
 * Format an IANA time zone's current UTC offset as "UTC±HH:MM", or `null` when the platform reports no GMT offset for the zone.
 */ function formatUtcOffset(timeZone, atDate) {
    const raw = readGmtOffsetString(timeZone, atDate);
    if (!raw) {
        return null;
    }
    // longOffset returns e.g. "GMT-08:00"; normalize to "UTC-08:00". A bare "GMT" (no numeric offset) is UTC+00:00.
    return raw === "GMT" ? "UTC+00:00" : raw.replace(/^GMT/, "UTC");
}
/** Like {@link formatUtcOffset} but falls back to "UTC+00:00" when the platform reports no GMT offset. */ function formatUtcOffsetWithDefault(timeZone, atDate) {
    return formatUtcOffset(timeZone, atDate) ?? "UTC+00:00";
}
/** Build the compact name and offset used by time-zone selection controls. */ export function getTimeZoneDisplayParts(timeZone, now = new Date(), locale) {
    try {
        return {
            name: genericTimeZoneName(timeZone, now, locale) ?? getTimeZoneCity(timeZone),
            utcOffset: formatUtcOffsetWithDefault(timeZone, now)
        };
    } catch (error) {
        if (!(error instanceof RangeError)) {
            throw error;
        }
        return {
            name: getTimeZoneCity(timeZone),
            utcOffset: "UTC+00:00"
        };
    }
}
/**
 * Build a fixed anchor `Date` used by the working-hours TimePicker. The exact
 * day doesn't matter – TimePicker only uses the date portion to construct its
 * dropdown options – so we pin to local midnight today.
 */ export function buildWorkingHoursDateAnchor(now = new Date()) {
    const anchor = new Date(now);
    anchor.setHours(0, 0, 0, 0);
    return anchor;
}
/** Project a `Temporal.PlainTime` onto the given anchor date. */ export function plainTimeToDate(anchor, time) {
    const d = new Date(anchor);
    d.setHours(time.hour, time.minute, 0, 0);
    return d;
}
/** Convert a `Date`'s hour/minute into a `Temporal.PlainTime`. */ export function dateToPlainTime(d) {
    return Temporal.PlainTime.from({
        hour: d.getHours(),
        minute: d.getMinutes()
    });
}
/**
 * Build the list of time zone options from the platform's IANA database.
 * The region prefix (e.g. "Pacific Time") is localized via `Intl` using the
 * app's current locale. Labels are formatted as "(UTC-08:00) Pacific Time - Los Angeles" when
 * a generic display name is available, or "Los Angeles (UTC-08:00)" otherwise.
 * Sorted by current UTC offset ascending, then alphabetically.
 */ export function buildTimeZoneOptions(traceLogger, now = new Date(), _locale) {
    try {
        const locale = getCurrentLocale();
        const ids = Intl.supportedValuesOf("timeZone");
        return ids.map((id)=>{
            const city = getTimeZoneCity(id);
            const region = genericTimeZoneName(id, now);
            const offsetLabel = formatUtcOffsetWithDefault(id, now);
            const label = region ? `(${offsetLabel}) ${region} - ${city}` : `(${offsetLabel}) ${city}`;
            return {
                value: id,
                label,
                offset: getUtcOffsetMinutes(id, now)
            };
        }).sort((a, b)=>a.offset - b.offset || a.label.localeCompare(b.label, locale)).map(({ value, label })=>({
                value,
                label
            }));
    } catch (error) {
        traceLogger.logTrace(0x1d85f34e /* tag_375no */ , TraceLevel.Error, `Error occurred while building time zone options: ${error}`);
        return [];
    }
}
/**
 * Format a single time zone for display in prose (e.g. the plan calendar nudge) as "(UTC-07:00) Pacific Time - Los Angeles".
 * Falls back to "(UTC-07:00) Los Angeles" when the platform has no generic region name, and to just the region/city when no offset is available.
 * @param timeZone IANA time zone id (e.g. "America/Los_Angeles").
 * @param now Moment used to resolve the current UTC offset (defaults to now).
 */ export function getTimeZoneDisplayLabel(timeZone, now = new Date(), _locale) {
    const city = getTimeZoneCity(timeZone);
    const region = genericTimeZoneName(timeZone, now);
    const namePart = region ? `${region} - ${city}` : city;
    // formatUtcOffset normalizes the platform's "GMT..." offset to "UTC..." (and "GMT" → "UTC+00:00"),
    // returning null when the zone has no GMT offset so we skip the "(UTC...)" prefix.
    const offset = formatUtcOffset(timeZone, now);
    if (offset == null) {
        return namePart;
    }
    return `(${offset}) ${namePart}`;
}
/** Default working hours start of day ("09:00"). */ export const DefaultWorkingHoursStartOfDay = Temporal.PlainTime.from("09:00");
/** Default working hours end of day ("17:00"). */ export const DefaultWorkingHoursEndOfDay = Temporal.PlainTime.from("17:00");
